import { useState, useEffect } from 'react';
import { useBrand } from '../lib/brand-context.js';
import { apiGet } from '../lib/api.js';
import { CHART_INK } from './chartTheme.js';

interface TypeDist {
  type: string;
  domain_count: number;
  retrieval_share: number;
  citation_share: number;
}

export function TypeDistributionChart() {
  const { selectedBrand, user } = useBrand();
  const [data, setData] = useState<TypeDist[]>([]);

  useEffect(() => {
    if (!selectedBrand || !user) return;
    let cancelled = false;
    async function load() {
      try {
        const url = `/api/v1/tenants/${selectedBrand!.tenant_id}/brands/${selectedBrand!.id}/sources/types`;
        const result = await apiGet<{ data: TypeDist[] }>(url);
        if (!cancelled) setData(result.data);
      } catch {
        // ignore
      }
    }
    load();
    return () => { cancelled = true; };
  }, [selectedBrand, user]);

  if (data.length === 0) return null;

  return (
    // Spec 234: no shadow, and no border on this rounded-lg panel — colour is
    // retired from the type dots too. Each dot is uniform ink; the type name
    // beneath it is the identity, exactly like every retired engine chip.
    <div className="rounded-lg bg-white p-4" data-testid="type-distribution">
      <h3 className="mb-3 text-sm font-semibold text-slate-700">Source type distribution</h3>
      <div className="flex gap-4 overflow-x-auto">
        {data.map((item) => (
          <div key={item.type} className="flex flex-col items-center min-w-[80px]">
            <div className="h-8 w-8 rounded-full" style={{ backgroundColor: CHART_INK }} />
            <span className="mt-1 text-xs font-medium text-slate-700">{item.type}</span>
            <span className="text-xs text-slate-500">{item.domain_count} {item.domain_count === 1 ? 'domain' : 'domains'}</span>
          </div>
        ))}
      </div>
    </div>
  );
}
